import { Component, Input, Output, EventEmitter, HostBinding, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

/**
 * A reusable button component that supports multiple visual variants and states.
 *
 * @remarks This component provides a standardized button interface with support
 * for different visual styles, accessibility features, and interaction states.
 * Always provide an `ariaLabel` for accessibility, use `type="link"` for navigation
 * actions, and disable the button during async operations.
 *
 * @example
 * ```html
 * <app-button
 *   type="primary"
 *   text="Submit"
 *   ariaLabel="Submit the form"
 *   [disabled]="isLoading"
 *   (clicked)="onSubmit()">
 * </app-button>
 * ```
 */
@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent {
  /**
   * The visual variant of the button.
   * - `primary`: Main action button with filled background
   * - `secondary`: Secondary action with outlined style
   * - `link`: Text-only button for navigation
   */
  @Input() type: 'primary' | 'secondary' | 'link' = 'primary';

  /**
   * Whether the button is disabled and non-interactive.
   * When true, the button cannot be clicked and shows disabled styling.
   */
  @Input() disabled = false;

  /**
   * The text content displayed inside the button.
   * Should be concise and action-oriented (e.g., "Save", "Cancel", "Learn More").
   */
  @Input() text = '';

  /**
   * Accessible label for screen readers.
   * Should describe the button's action clearly, especially when `text` is not descriptive enough.
   */
  @Input() ariaLabel = '';

  /**
   * Event emitted when the button is clicked.
   * Only fires if the button is not disabled.
   */
  @Output() clicked = new EventEmitter<void>();

  /**
   * Prevents the button from receiving focus via tab navigation.
   * Focus should be managed by the button's internal elements.
   */
  @HostBinding('attr.tabindex') tabindex = '-1';

  /**
   * Handles button click events.
   * Prevents default browser behavior and emits the clicked event if not disabled.
   *
   * @param event - The native click event
   */
  onClick(event: Event): void {
    if (!this.disabled) {
      this.clicked.emit();
    }
    event.preventDefault();
  }
}
